import React from 'react';
import { BuildingIcon, Building2Icon, Columns3Icon, FileSignatureIcon, HomeIcon, KeyRoundIcon, RefreshCwIcon, SearchIcon, SunIcon, TrendingUpIcon, HandshakeIcon } from 'lucide-react';
import { StepFrame, FieldGrid } from '../StepFrame';
import { ChoiceCard } from '../../ui/ChoiceCard';
import { HelpPanel } from '../../ui/HelpPanel';
import { TextField } from '../../ui/TextField';
import { AnimatedNumber } from '../../ui/AnimatedNumber';
import { usePortal } from '../../../contexts/PortalContext';
import { loanOfficer } from '../../../data/team';
import { formatAmountInput, formatCurrency, parseAmount } from '../../../utils/format';
const icon = 'h-5 w-5';
export function GoalStep() {
  const {
    answers,
    update
  } = usePortal();
  return <StepFrame title="What are you looking to do?" description="This shapes everything else we ask, so we only ask what matters to you." data-id="element-308" data-mp="3go7ck1g9ssb9">
      <div role="radiogroup" className="grid gap-3 md:grid-cols-2" data-id="element-309" data-mp="3lm8u2hy1sjkm">
        <ChoiceCard layout="stack" icon={<HomeIcon className={icon} data-id="element-311" data-mp="gljvl479sksf" />} title="Buy a home" description="Get a mortgage for a new purchase." selected={answers.goal === 'purchase'} onSelect={() => update({
        goal: 'purchase'
      })} data-id="element-310" data-mp="gmd6mhd9hwgd" />
        <ChoiceCard layout="stack" icon={<RefreshCwIcon className={icon} data-id="element-313" data-mp="7sgmt97kkw44" />} title="Refinance" description="Lower your rate or take cash out." selected={answers.goal === 'refinance'} onSelect={() => update({
        goal: 'refinance'
      })} data-id="element-312" data-mp="7t9j1g8wk380" />
      </div>
    </StepFrame>;
}
export function StageStep() {
  const {
    answers,
    update
  } = usePortal();
  return <StepFrame title="Have you found a home yet?" data-id="element-314" data-mp="3p4rg0vpwydj1">
      <div role="radiogroup" className="grid gap-3" data-id="element-315" data-mp="3l1up8hq7zb40">
        <ChoiceCard icon={<FileSignatureIcon className={icon} data-id="element-317" data-mp="3g203eslaxbpt" />} title="Yes, I’m under contract" description="You’ve signed a purchase agreement." selected={answers.propertyStage === 'contract'} onSelect={() => update({
        propertyStage: 'contract'
      })} data-id="element-316" data-mp="3g1q8qrpbagds" />
        <ChoiceCard icon={<HandshakeIcon className={icon} data-id="element-319" data-mp="1eb51dj0i4gan" />} title="I’m making offers" description="You’ve found homes you like and are negotiating." selected={answers.propertyStage === 'offers'} onSelect={() => update({
        propertyStage: 'offers'
      })} data-id="element-318" data-mp="1ebojp0u2d6p7" />
        <ChoiceCard icon={<SearchIcon className={icon} data-id="element-321" data-mp="1oetvdta6re1d" />} title="Still looking" description="We’ll help you get pre-approved first." selected={answers.propertyStage === 'looking'} onSelect={() => update({
        propertyStage: 'looking'
      })} data-id="element-320" data-mp="1oeag6kbx73r1" />
      </div>
    </StepFrame>;
}
export function TypeStep() {
  const {
    answers,
    update
  } = usePortal();
  const opts = [{
    v: 'single',
    t: 'Single-family home',
    d: 'A detached house',
    i: <HomeIcon className={icon} data-id="element-322" data-mp="275l02hj8ag9f" />
  }, {
    v: 'townhouse',
    t: 'Townhouse',
    d: 'Shares a wall with neighbors',
    i: <Columns3Icon className={icon} data-id="element-323" data-mp="2c2tm4t7hz4fy" />
  }, {
    v: 'condo',
    t: 'Condo',
    d: 'A unit in a shared building',
    i: <Building2Icon className={icon} data-id="element-324" data-mp="vol8xp8ma7gz" />
  }, {
    v: 'multi',
    t: '2–4 units',
    d: 'A duplex, triplex, or fourplex',
    i: <BuildingIcon className={icon} data-id="element-325" data-mp="10piv0cwqzlnr" />
  }] as const;
  return <StepFrame title="What type of property is it?" data-id="element-326" data-mp="t4oxbbsit09a">
      <div role="radiogroup" className="grid gap-3 md:grid-cols-2" data-id="element-327" data-mp="t7b61er5dlt3">
        {opts.map(o => <ChoiceCard key={o.v} layout="stack" icon={o.i} title={o.t} description={o.d} selected={answers.propertyType === o.v} onSelect={() => update({
        propertyType: o.v
      })} data-id="element-328" data-mp="o5u2tcl6xn9c" />)}
      </div>
    </StepFrame>;
}
export function UseStep() {
  const {
    answers,
    update
  } = usePortal();
  return <StepFrame title="How will you use this home?" help={<>
          <HelpPanel question="What does primary residence mean?" data-id="element-330" data-mp="5hgud0ike3af">
            It’s the home you’ll live in most of the year — usually moving in within 60 days of closing. Primary residences typically get the best rates and lowest down payments.
          </HelpPanel>
          <HelpPanel question="What if I plan to rent out part of it?" data-id="element-331" data-mp="3mvcabqgzdzcn">
            If you’ll live in one unit and rent the others, choose primary residence. {loanOfficer.firstName} will confirm the details with you.
          </HelpPanel>
        </>} data-id="element-329" data-mp="9f4ssdjgue72">
      <div role="radiogroup" className="grid gap-3" data-id="element-332" data-mp="4dgdmcbienc0">
        <ChoiceCard icon={<KeyRoundIcon className={icon} data-id="element-334" data-mp="4dq4010guwi8" />} title="Primary residence" description="I’ll live here full-time." selected={answers.occupancy === 'primary'} onSelect={() => update({
        occupancy: 'primary'
      })} data-id="element-333" data-mp="4e8bjtskwx5g" />
        <ChoiceCard icon={<SunIcon className={icon} data-id="element-336" data-mp="e530j5x7maul" />} title="Second home" description="A vacation or part-time home." selected={answers.occupancy === 'second'} onSelect={() => update({
        occupancy: 'second'
      })} data-id="element-335" data-mp="e4kw3m0e8qbl" />
        <ChoiceCard icon={<TrendingUpIcon className={icon} data-id="element-338" data-mp="s3apaqk3e5uo" />} title="Investment property" description="I’ll rent it out." selected={answers.occupancy === 'investment'} onSelect={() => update({
        occupancy: 'investment'
      })} data-id="element-337" data-mp="n5rc7tz4rz4b" />
      </div>
    </StepFrame>;
}
export function PriceStep() {
  const {
    answers,
    update
  } = usePortal();
  const pct = answers.price ? Math.round(answers.downPayment / answers.price * 100) : 0;
  const loan = Math.max(0, answers.price - answers.downPayment);
  const isPurchase = answers.goal === 'purchase';
  return <StepFrame title={isPurchase ? 'Your price and down payment' : 'Your home’s value'} description="An estimate is fine — we’ll confirm exact numbers later." help={<HelpPanel question="How much should I put down?" data-id="element-340" data-mp="127r4dhpqzixf">
          Many loans allow as little as 3–5% down. Putting 20% or more down usually means you won’t pay mortgage insurance. {loanOfficer.firstName} can show you both side by side.
        </HelpPanel>} data-id="element-339" data-mp="1b6l8dt5erbl0">
      <TextField label={isPurchase ? 'Purchase price' : 'Estimated home value'} prefix="$" inputMode="numeric" value={formatAmountInput(String(answers.price))} onChange={v => {
      const price = parseAmount(v);
      update({
        price,
        downPayment: Math.min(answers.downPayment, price)
      });
    }} data-id="element-341" data-mp="1pwk4zrhbeeyd" />

      <div className="mt-8" data-id="element-342" data-mp="9gplb5ixp38m">
        <div className="flex items-baseline justify-between" data-id="element-343" data-mp="9kn7k00t6awq">
          <label htmlFor="down" className="text-sm font-medium text-ink-700" data-id="element-344" data-mp="b0r7mzgff0se">
            Down payment
          </label>
          <span className="tabular text-sm text-ink-500" data-id="element-345" data-mp="fxrcj71jhxhx">{pct}%</span>
        </div>
        <p className="tabular mt-1 text-2xl font-semibold tracking-tight text-ink-900" data-id="element-346" data-mp="1y9l0jwv064v">{formatCurrency(answers.downPayment)}</p>
        <input id="down" type="range" min={3} max={50} value={pct} onChange={e => update({
        downPayment: Math.round(answers.price * Number(e.target.value) / 100 / 1000) * 1000
      })} className="mt-4 w-full accent-pulse-600" data-id="element-347" data-mp="6zp6gd2y8ens" />
        <div className="mt-1 flex justify-between text-xs text-ink-400" data-id="element-348" data-mp="3fbutfsru53q4">
          <span data-id="element-349" data-mp="3jfj26es7e5cy">3%</span>
          <span data-id="element-350" data-mp="3jcwxqo0m05yy">20%</span>
          <span data-id="element-351" data-mp="35eyjrjq7abjz">50%</span>
        </div>
      </div>

      <div className="mt-8 flex items-center justify-between rounded-2xl bg-ink-900 px-5 py-5 text-white" data-id="element-352" data-mp="35g9uy4lz6ll4">
        <div data-id="element-353" data-mp="3agnsuttv4f5f">
          <p className="text-sm text-white/60" data-id="element-354" data-mp="2qst45455swu9">Estimated loan amount</p>
          <AnimatedNumber value={loan} className="mt-1 block text-3xl font-semibold tracking-tight" data-id="element-355" data-mp="2qt1fuspjpw3v" />
        </div>
        <span className={`rounded-full px-3 py-1 text-xs font-medium ${pct >= 20 ? 'bg-positive-500/20 text-[#7EE2B5]' : 'bg-white/10 text-white/80'}`} data-id="element-356" data-mp="2lw2o2gtcu18u">
          {pct >= 20 ? 'No mortgage insurance' : 'Mortgage insurance likely'}
        </span>
      </div>
    </StepFrame>;
}
export function AddressStep() {
  const {
    answers,
    update
  } = usePortal();
  return <StepFrame title={answers.goal === 'refinance' ? 'Which home are you refinancing?' : 'Where’s the property?'} description={answers.goal === 'refinance' ? 'The address of the home with the mortgage you’re replacing.' : 'Use the address from your purchase agreement.'} data-id="element-357" data-mp="34rsow0pjwrl5">
      <div className="space-y-4" data-id="element-358" data-mp="3sj6h86npcn64">
        <TextField label="Street address" value={answers.propertyStreet} onChange={v => update({
        propertyStreet: v
      })} autoComplete="street-address" data-id="element-359" data-mp="3sg29x0exxbb8" />
        <div className="grid grid-cols-6 gap-4" data-id="element-360" data-mp="yqwpueuwfqmc">
          <TextField className="col-span-6 sm:col-span-3" label="City" value={answers.propertyCity} onChange={v => update({
          propertyCity: v
        })} data-id="element-361" data-mp="yshi1xtqqxh1" />
          <TextField className="col-span-2 sm:col-span-1" label="State" value={answers.propertyState} onChange={v => update({
          propertyState: v
        })} maxLength={2} data-id="element-362" data-mp="18o18i4pjd3ye" />
          <TextField className="col-span-4 sm:col-span-2" label="ZIP" inputMode="numeric" value={answers.propertyZip} onChange={v => update({
          propertyZip: v
        })} data-id="element-363" data-mp="fzm0w38qhnx3" />
        </div>
        {answers.goal === 'purchase' && <FieldGrid data-id="element-364" data-mp="ozsbnhd1s9sk">
            <TextField label="Expected closing date" type="date" value={answers.closingDate} onChange={v => update({
          closingDate: v
        })} hint="It’s on your contract." data-id="element-365" data-mp="ozihriovtihl" />
          </FieldGrid>}
      </div>
    </StepFrame>;
}